import { useLocation, useNavigate } from 'react-router';

import { apiCreateTmpStorage } from '#/api/tmpStorage/apiCreateTmpStorage';
import type {
  ApiUser,
  ApiUserEnvironment,
} from '#/api/userEnvironment/types/ApiUserEnvironment';
import { EmailSignupForm } from '#/components/Email/EmailSignupForm';
import type { EmailSignupFormValues } from '#/components/Email/types/EmailSignupFormValues';
import type { FormUsageContext } from '#/components/Form/Form';
import type { FormSubmitFunction } from '#/components/Form/FormContext';
import { useUserEnvironmentActions } from '#/contexts/UserEnvironmentContext';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { AuthApiTmpStorage } from '#/modules/auth/api/tmpStorage/types/AuthApiTmpStorage';
import { authLoginRoute } from '#/modules/auth/routing/auth.routes';
import type { NonNullableFields } from '#/types/NonNullableFields';
import { trackSignUp } from '#/utils/analytics/track/trackSignUp';
import { configureTrackJs } from '#/utils/configureTrackJs';
import { getThirdPartyLoginUrl } from '#/utils/getThirdPartyLoginUrl';
import { postJson } from '#/utils/http/postJson';
import { HttpError } from '#/utils/HttpError';
import { resetRecaptcha } from '#/utils/recaptcha';
import { generateRedirectUrl } from '#/utils/temporaryStorage';
import { showModal } from '#/zustand-stores/modalStore';

type Props = {
  analyticsTitle: string;
  usageContext: FormUsageContext;
  redirectTo?: string | null;
  tmpStorage?: AuthApiTmpStorage;
  onSignup?: () => void;
  onLogin?: (arg0: ApiUser) => void;
  loginOverrideFn?: () => void;
  compactView?: boolean;
  hideLoginBlock?: boolean;
  customButtonText?: string;
  setEmailInUse?: (emailInUse: string) => void;
  requirePassword?: boolean;
};

export function EmailSignupFormContainer({
  analyticsTitle,
  usageContext,
  redirectTo,
  tmpStorage,
  onSignup,
  onLogin,
  loginOverrideFn,
  compactView,
  hideLoginBlock,
  customButtonText,
  setEmailInUse,
  requirePassword,
}: Props) {
  const { i18n, locale } = useI18n();

  const initialValues = {
    email: '',
    recaptchaKey: 0,
    gRecaptchaResponse: undefined,
  };

  const location = useLocation();
  const navigate = useNavigate();
  const { setUserEnvironment } = useUserEnvironmentActions();

  const showLoginModal = (email?: string, initialWarning?: string) => {
    showModal('LOGIN', {
      redirectTo:
        (tmpStorage?.type === 'redirect' ? tmpStorage.redirectUrl : null) ??
        redirectTo,
      tmpStorage,
      email,
      onLogin,
      initialWarning,
    });
  };

  const getLoginUrlAndState = (email?: string) => ({
    to: authLoginRoute.with({}),
    state: {
      redirectTo:
        (tmpStorage?.type === 'redirect' ? tmpStorage.redirectUrl : null) ??
        redirectTo,
      tmpStorage,
      email,
    },
  });

  const handleThirdPartyLoginUrl = (
    provider: 'facebook' | 'google' | 'apple',
  ) =>
    getThirdPartyLoginUrl({
      locale,
      provider,
      redirectTo:
        redirectTo ??
        (tmpStorage?.type === 'redirect' ? tmpStorage.redirectUrl : null) ??
        `${location.pathname}${location.search}`,
      tmpStorage,
    });

  const onSubmit: FormSubmitFunction<EmailSignupFormValues> = async ({
    values,
    handleError,
    updateSubmitting,
  }) => {
    const { email, recaptchaKey, gRecaptchaResponse, password } = values;

    try {
      const tmpStorageId = tmpStorage
        ? await apiCreateTmpStorage(tmpStorage)
        : null;

      const endpoint = requirePassword
        ? '/data/website/signup-email-password'
        : '/data/website/signup-email';
      const userEnvironment = await postJson<
        NonNullableFields<ApiUserEnvironment>
      >(endpoint, {
        email,
        password,
        gRecaptchaResponse,
        redirectTo: generateRedirectUrl(
          redirectTo ??
            `${location.pathname}${location.search}${location.hash}`,
          tmpStorageId,
        ),
      });
      trackSignUp(userEnvironment.user, 'Email');
      configureTrackJs(userEnvironment.user?.id);
      setUserEnvironment(userEnvironment);

      if (onSignup) {
        onSignup();
      }

      updateSubmitting(false);

      if (redirectTo) {
        void navigate(redirectTo);
      }
    } catch (error) {
      if (error instanceof HttpError) {
        const errorArray = error.responseJson?.errors;
        const emailInUseError = errorArray?.find(
          (e) => e.name === 'email' && e.type === 'alreadyInUse',
        );
        const vmVerifyEmailError = error.responseJson?.errors?.find(
          (e) => e.type === 'vmVerifyEmail',
        );

        if (emailInUseError) {
          if (setEmailInUse) {
            setEmailInUse(email);
          } else {
            showLoginModal(
              email,
              /* TRANSLATORS: Login modal error message */
              i18n(
                'An account with this email already exists. Did you mean to login?',
              ),
            );
          }
        } else if (vmVerifyEmailError) {
          // Show VM email verification modal instead of form error
          showModal('VM_EMAIL_VERIFICATION', {
            email: values.email,
          });
          return;
        } else {
          resetRecaptcha();
          handleError(error, {
            email,
            recaptchaKey: recaptchaKey + 1,
            gRecaptchaResponse: undefined,
          });
        }

        updateSubmitting(false);
      }
    }
  };

  return (
    <EmailSignupForm
      analyticsTitle={analyticsTitle}
      usageContext={usageContext}
      initialValues={initialValues}
      showLoginModal={showLoginModal}
      getLoginUrlAndState={getLoginUrlAndState}
      getThirdPartyLoginUrl={handleThirdPartyLoginUrl}
      loginOverrideFn={loginOverrideFn}
      onSubmit={onSubmit}
      compactView={compactView}
      hideLoginBlock={hideLoginBlock}
      customButtonText={customButtonText}
      requirePassword={requirePassword}
    />
  );
}
